/**
 * Skeleton loading primitives (states/spec §1). A shimmering `--td-surface-2` block that reserves
 * layout while an async region loads; on `prefers-reduced-motion` the shimmer is dropped for a
 * static muted block (handled in CSS). Skeletons are DECORATIVE (`aria-hidden`) — the enclosing
 * region carries `role="status"` + `aria-busy` and a visually-hidden "Loading…" so screen-reader
 * users still get the status without hearing a wall of placeholder boxes. Use <SkeletonRegion> as
 * that wrapper; use <Skeleton> for individual bars.
 */
import type { CSSProperties, ReactNode } from "react";

interface SkeletonProps {
  /** CSS width, e.g. "100%", "8ch", "3rem". Defaults to full width. */
  width?: string;
  /** CSS height. Defaults to a single text line. */
  height?: string;
  /** Pill/circle vs the default rounded bar. */
  variant?: "bar" | "pill" | "circle";
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({
  width,
  height,
  variant = "bar",
  className,
  style,
}: SkeletonProps): JSX.Element {
  const cls = ["td-skel", `td-skel--${variant}`, className ?? ""].filter(Boolean).join(" ");
  return <span className={cls} style={{ width, height, ...style }} aria-hidden="true" />;
}

interface SkeletonRegionProps {
  /** Visually-hidden text announced to SR users, e.g. "Loading concerns…". */
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * Wraps a group of skeletons with the correct a11y semantics: a live `role="status"` region with
 * `aria-busy="true"` and a screen-reader-only label. The skeleton visuals inside stay aria-hidden.
 */
export function SkeletonRegion({ label, className, children }: SkeletonRegionProps): JSX.Element {
  return (
    <div className={className} role="status" aria-busy="true">
      <span className="td-sr-only">{label}</span>
      {children}
    </div>
  );
}
